import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Editor, { DiffEditor } from "@monaco-editor/react";
import { toast } from "sonner";
import { api, runAgent, assemblePreview } from "@/lib/api";
import { Logo } from "@/components/Logo";
import OwnerStudio from "@/components/workspace/OwnerStudio";
import {
  ResizablePanelGroup, ResizablePanel, ResizableHandle,
} from "@/components/ui/resizable";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Files, GitBranch, KeyRound, Search as SearchIcon, Send, ArrowLeft,
  Monitor, Tablet, Smartphone, RefreshCw, ExternalLink, Terminal as TermIcon,
  FileCode, Save, GitCompare, RotateCcw, X, Trash2, Plus, Wand2, ChevronRight,
  Sparkles, Layers, Code2, Bug, TestTube2, ShieldCheck, Rocket, Loader2, CheckCircle2,
  AppWindow, Globe, Copy, Lightbulb, ChevronDown, Download, Database,
} from "lucide-react";

const PLATFORM_META = {
  website: { label: "Website", icon: Globe }, webapp: { label: "Web App", icon: AppWindow },
  mobile: { label: "Mobile App", icon: Smartphone },
};

const AGENT_ICON = { Product: Sparkles, Architect: Layers, Coding: Code2, Debugging: Bug, Testing: TestTube2, Security: ShieldCheck, Deployment: Rocket };
const AGENT_COLOR = { Product: "text-indigo-400", Architect: "text-cyan-400", Coding: "text-emerald-400", Debugging: "text-amber-400", Testing: "text-purple-400", Security: "text-rose-400", Deployment: "text-blue-400" };

function langOf(path) {
  if (path.endsWith(".html")) return "html";
  if (path.endsWith(".css")) return "css";
  if (path.endsWith(".js")) return "javascript";
  if (path.endsWith(".json")) return "json";
  if (path.endsWith(".md")) return "markdown";
  return "plaintext";
}

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [baseline, setBaseline] = useState({});
  const [activeFile, setActiveFile] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [sideTab, setSideTab] = useState("files");
  const [centerTab, setCenterTab] = useState("agents");
  const [messages, setMessages] = useState([]);
  const [stream, setStream] = useState([]);
  const [running, setRunning] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [versions, setVersions] = useState([]);
  const [envVars, setEnvVars] = useState([]);
  const [search, setSearch] = useState("");
  const [viewport, setViewport] = useState("desktop");
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [showConsole, setShowConsole] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [diffMode, setDiffMode] = useState(false);
  const [linked, setLinked] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [studioOpen, setStudioOpen] = useState(false);
  const [dataCollections, setDataCollections] = useState([]);
  const [dataActive, setDataActive] = useState(null);
  const [dataRows, setDataRows] = useState([]);
  const streamEndRef = useRef(null);
  const startedRef = useRef(false);
  const vpInitRef = useRef(false);

  const loadAll = useCallback(async () => {
    const [p, f, m, v, e] = await Promise.all([
      api.get(`/projects/${id}`), api.get(`/projects/${id}/files`),
      api.get(`/projects/${id}/messages`), api.get(`/projects/${id}/versions`),
      api.get(`/projects/${id}/env`),
    ]);
    setProject(p.data); setFiles(f.data);
    setBaseline(Object.fromEntries(f.data.map((x) => [x.path, x.content])));
    setMessages(m.data); setVersions(v.data); setEnvVars(e.data);
    setLinked(p.data.linked || []);
    if (!vpInitRef.current) { vpInitRef.current = true; if (p.data.platform === "mobile") setViewport("mobile"); }
    if (f.data.length && !activeFile) { setActiveFile(f.data[0].path); setTabs([f.data[0].path]); }
  }, [id]); // eslint-disable-line

  useEffect(() => { loadAll().catch(() => toast.error("Failed to load project")); }, [loadAll]);

  useEffect(() => {
    const handler = (ev) => {
      if (ev.data && ev.data.__dcode) setConsoleLogs((l) => [...l, { level: ev.data.level, msg: ev.data.msg, t: Date.now() }]);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => { streamEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [stream, messages]);

  const execRun = useCallback(async (text) => {
    if (!text.trim() || running) return;
    setRunning(true); setCenterTab("agents"); setStream([]); setConsoleLogs([]);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setProject((p) => p && { ...p, status: "building" });
    const changed = {};
    try {
      await runAgent(id, text, (ev) => {
        setStream((s) => [...s, ev]);
        if (ev.type === "file_written") {
          changed[ev.path] = true;
          setFiles((fs) => {
            const i = fs.findIndex((x) => x.path === ev.path);
            if (i >= 0) { const c = [...fs]; c[i] = { ...c[i], content: ev.content }; return c; }
            return [...fs, { path: ev.path, content: ev.content }];
          });
        }
        if (ev.type === "file_deleted") setFiles((fs) => fs.filter((x) => x.path !== ev.path));
        if (ev.type === "files_snapshot") {
          setFiles(ev.files);
          if (!activeFile && ev.files[0]) { setActiveFile(ev.files[0].path); setTabs([ev.files[0].path]); }
        }
        if (ev.type === "task_done") {
          if (ev.summary) setMessages((m) => [...m, { role: "assistant", content: ev.summary }]);
        }
      });
      await loadAll();
      setPreviewKey((k) => k + 1);
      toast.success("Build complete");
    } catch (e) {
      toast.error("Agent run failed");
    } finally { setRunning(false); }
  }, [id, running, activeFile, loadAll]);

  useEffect(() => {
    if (location.state?.autoRun && !startedRef.current) {
      startedRef.current = true;
      execRun(location.state.autoRun);
      window.history.replaceState({}, "");
    }
  }, [location.state, execRun]);

  const openFile = (path) => { setActiveFile(path); setCenterTab("code"); setDiffMode(false); if (!tabs.includes(path)) setTabs((t) => [...t, path]); };
  const closeTab = (e, path) => {
    e.stopPropagation();
    const nt = tabs.filter((t) => t !== path); setTabs(nt);
    if (activeFile === path) setActiveFile(nt[nt.length - 1] || null);
  };
  const onEdit = (val) => setFiles((fs) => fs.map((f) => (f.path === activeFile ? { ...f, content: val ?? "" } : f)));
  const saveFile = async () => {
    const f = files.find((x) => x.path === activeFile);
    if (!f) return;
    await api.put(`/projects/${id}/files`, { path: f.path, content: f.content });
    setPreviewKey((k) => k + 1);
    toast.success("Saved");
  };
  const restore = async (v) => {
    if (!window.confirm(`Restore to version ${v}? Current state is snapshotted first.`)) return;
    await api.post(`/projects/${id}/versions/${v}/restore`);
    await loadAll(); setPreviewKey((k) => k + 1); toast.success(`Restored v${v}`);
  };
  const fixError = async (msg) => {
    toast.info("Debugging Agent analyzing error...");
    try {
      const { data } = await api.post(`/projects/${id}/debug`, { error: msg, file: activeFile || "" });
      setFiles(data.files); setPreviewKey((k) => k + 1); setConsoleLogs([]);
      toast.success(`Fixed: ${data.diagnosis?.slice(0, 60) || "applied patch"}`);
    } catch (e) { toast.error("Debug failed"); }
  };
  const addEnv = async () => {
    const key = window.prompt("Variable name (e.g. API_URL)"); if (!key) return;
    const value = window.prompt("Value") || "";
    await api.post(`/projects/${id}/env`, { key, value }); setEnvVars((e) => [...e.filter((x) => x.key !== key), { key, value }]);
  };
  const delEnv = async (key) => { await api.delete(`/projects/${id}/env?key=${encodeURIComponent(key)}`); setEnvVars((e) => e.filter((x) => x.key !== key)); };

  const loadSuggest = useCallback(() => {
    api.post(`/projects/${id}/suggest`).then((r) => setSuggestions(r.data.suggestions || [])).catch(() => {});
  }, [id]);
  useEffect(() => { if (project?.status) loadSuggest(); }, [project?.status, loadSuggest]);

  const createCounterpart = async (platform) => {
    try {
      const { data } = await api.post(`/projects/${id}/link`, { platform });
      toast.success(`${PLATFORM_META[platform].label} copy created — shares the same code & data`);
      navigate(`/workspace/${data.id}`);
    } catch (e) { toast.error("Could not create copy"); }
  };

  const downloadPackage = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/export`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(project?.name || "project").replace(/[^A-Za-z0-9_-]/g, "-")}.zip`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Package downloaded");
    } catch (e) { toast.error("Build the app first, then download"); }
  };

  const loadDataCollections = useCallback(() => {
    api.get(`/projects/${id}/appdata`).then((r) => setDataCollections(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { if (sideTab === "data") loadDataCollections(); }, [sideTab, loadDataCollections, project?.status]);
  const openCollection = async (c) => { setDataActive(c); try { const r = await api.get(`/projects/${id}/appdata/${c}`); setDataRows(r.data); } catch (e) { /* noop */ } };
  const deleteRow = async (c, rid) => { await api.delete(`/projects/${id}/appdata/${c}/${rid}`); openCollection(c); };

  const activeContent = files.find((f) => f.path === activeFile)?.content ?? "";
  const sid = project?.source_id || id;
  const previewDoc = assemblePreview(files, {
    dataBase: `${process.env.REACT_APP_BACKEND_URL}/api/apps/${sid}/data`,
    authBase: `${process.env.REACT_APP_BACKEND_URL}/api/apps/${sid}/auth`,
    logo: project?.logo || "",
    extraCss: project?.overrides?.extra_css || "",
  });
  const vpWidth = { desktop: "100%", tablet: "768px", mobile: "375px" }[viewport];

  const filtered = search
    ? files.filter((f) => f.path.includes(search) || f.content.toLowerCase().includes(search.toLowerCase()))
    : files;

  return (
    <div className="h-screen flex flex-col bg-[#090D16] text-slate-100 overflow-hidden">
      {/* Top bar */}
      <div className="h-14 border-b border-slate-800/70 dc-glass flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <button data-testid="workspace-back" onClick={() => navigate("/dashboard/projects")} className="text-slate-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
          <Logo />
          <ChevronRight className="h-4 w-4 text-slate-600" />
          <span className="font-head font-medium truncate max-w-[240px]">{project?.name || "Loading..."}</span>
          {running && <span className="flex items-center gap-1.5 text-xs text-amber-400"><Loader2 className="h-3.5 w-3.5 dc-spin" /> building</span>}
          {!running && project?.status === "ready" && <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> ready</span>}
        </div>
        <div className="flex items-center gap-2">
          {project?.platform && (() => { const P = PLATFORM_META[project.platform]; const I = P.icon; return (
            <span data-testid="platform-badge" className="hidden sm:flex items-center gap-1.5 text-xs px-2.5 h-8 rounded-lg border border-slate-700 text-slate-300"><I className="h-3.5 w-3.5 text-emerald-400" /> {P.label}</span>
          ); })()}
          {linked.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="linked-switcher" className="flex items-center gap-1.5 text-xs px-2.5 h-8 rounded-lg border border-slate-700 text-slate-300 hover:border-emerald-500/40"><Copy className="h-3.5 w-3.5" /> Linked ({linked.length}) <ChevronDown className="h-3 w-3" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-200">
                {linked.map((l) => { const I = PLATFORM_META[l.platform]?.icon || AppWindow; return (
                  <DropdownMenuItem key={l.id} data-testid={`open-linked-${l.id}`} onClick={() => navigate(`/workspace/${l.id}`)} className="gap-2 cursor-pointer focus:bg-slate-800"><I className="h-3.5 w-3.5" /> {l.name}</DropdownMenuItem>
                ); })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="create-copy" className="flex items-center gap-1.5 text-xs px-2.5 h-8 rounded-lg border border-slate-700 text-slate-300 hover:border-emerald-500/40"><Plus className="h-3.5 w-3.5" /> Create copy <ChevronDown className="h-3 w-3" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-200">
              {Object.entries(PLATFORM_META).map(([k, v]) => { const I = v.icon; return (
                <DropdownMenuItem key={k} data-testid={`copy-to-${k}`} onClick={() => createCounterpart(k)} className="gap-2 cursor-pointer focus:bg-slate-800"><I className="h-3.5 w-3.5" /> {v.label} copy</DropdownMenuItem>
              ); })}
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid="download-package" onClick={downloadPackage} className="flex items-center gap-1.5 text-xs px-2.5 h-8 rounded-lg border border-slate-700 text-slate-300 hover:border-emerald-500/40"><Download className="h-3.5 w-3.5" /> Download</button>
          <button data-testid="open-studio" onClick={() => setStudioOpen(true)} className="flex items-center gap-1.5 text-xs px-3 h-8 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold"><Wand2 className="h-3.5 w-3.5" /> Studio</button>
        </div>
      </div>

      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* LEFT SIDEBAR */}
        <ResizablePanel defaultSize={20} minSize={14} className="border-r border-slate-800/70">
          <div className="h-full flex flex-col bg-slate-950/40">
            <div className="flex border-b border-slate-800/70">
              {[["files", Files], ["git", GitBranch], ["env", KeyRound], ["data", Database], ["search", SearchIcon]].map(([k, Icon]) => (
                <button key={k} data-testid={`side-tab-${k}`} onClick={() => setSideTab(k)}
                  className={`flex-1 h-10 flex items-center justify-center transition-colors ${sideTab === k ? "text-emerald-400 border-b-2 border-emerald-400" : "text-slate-500 hover:text-slate-300"}`}>
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-auto dc-scroll p-2">
              {sideTab === "files" && (
                <div data-testid="file-explorer">
                  {files.length === 0 && <p className="text-xs text-slate-500 p-3">No files yet. Run the agent to generate code.</p>}
                  {files.map((f) => (
                    <button key={f.path} data-testid={`file-item-${f.path}`} onClick={() => openFile(f.path)}
                      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${activeFile === f.path ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:bg-slate-800/50"}`}>
                      <FileCode className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{f.path}</span>
                    </button>
                  ))}
                </div>
              )}
              {sideTab === "git" && (
                <div data-testid="git-panel" className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 px-2 py-1">Version history</div>
                  {versions.length === 0 && <p className="text-xs text-slate-500 p-2">No versions yet.</p>}
                  {versions.map((v) => (
                    <div key={v.id} data-testid={`version-${v.version}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-emerald-400">v{v.version}</span>
                        <button data-testid={`restore-${v.version}`} onClick={() => restore(v.version)} className="text-slate-500 hover:text-cyan-400" title="Restore"><RotateCcw className="h-3.5 w-3.5" /></button>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 line-clamp-2">{v.message}</p>
                      <p className="text-[10px] text-slate-600 mt-1">{new Date(v.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
              {sideTab === "env" && (
                <div data-testid="env-panel" className="space-y-2">
                  <button data-testid="add-env" onClick={addEnv} className="w-full h-8 rounded-lg border border-slate-700 text-xs flex items-center justify-center gap-1 hover:text-emerald-400"><Plus className="h-3.5 w-3.5" /> Add variable</button>
                  {envVars.map((e) => (
                    <div key={e.key} className="rounded-lg border border-slate-800 bg-slate-900/40 p-2 flex items-center justify-between">
                      <div className="min-w-0"><div className="text-xs font-mono text-cyan-400 truncate">{e.key}</div><div className="text-[10px] text-slate-500 truncate">••••••</div></div>
                      <button onClick={() => delEnv(e.key)} className="text-slate-600 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
              {sideTab === "data" && (
                <div data-testid="data-dashboard" className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 px-2 py-1">App data (live)</div>
                  {dataCollections.length === 0 && <p className="text-xs text-slate-500 p-2">No stored data yet. Data your app saves via the hosted API appears here.</p>}
                  {dataCollections.map((c) => (
                    <button key={c.collection} data-testid={`data-collection-${c.collection}`} onClick={() => openCollection(c.collection)}
                      className={`w-full text-left flex items-center justify-between px-2 py-1.5 rounded-md text-sm ${dataActive === c.collection ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:bg-slate-800/50"}`}>
                      <span className="flex items-center gap-2"><Database className="h-3.5 w-3.5" /> {c.collection}</span>
                      <span className="text-[10px] text-slate-500">{c.count}</span>
                    </button>
                  ))}
                  {dataActive && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between px-2 mb-1">
                        <span className="text-xs text-slate-400">{dataActive} · {dataRows.length}</span>
                        <button data-testid="export-data" onClick={() => { const blob = new Blob([JSON.stringify(dataRows, null, 2)], { type: "application/json" }); const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = `${dataActive}.json`; a.click(); URL.revokeObjectURL(u); }}
                          className="text-[10px] text-cyan-400 hover:underline flex items-center gap-1"><Download className="h-3 w-3" /> JSON</button>
                      </div>
                      <div className="space-y-1">
                        {dataRows.map((row) => (
                          <div key={row.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-2 text-[11px] font-mono flex items-start justify-between gap-2">
                            <span className="text-slate-300 break-all">{JSON.stringify(Object.fromEntries(Object.entries(row).filter(([k]) => k !== "id" && k !== "created_at")))}</span>
                            <button onClick={() => deleteRow(dataActive, row.id)} className="text-slate-600 hover:text-rose-400 shrink-0"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {sideTab === "search" && (
                <div data-testid="search-panel">
                  <input data-testid="code-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search files & code..."
                    className="w-full h-9 px-3 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none mb-2" />
                  {search && filtered.map((f) => (
                    <button key={f.path} onClick={() => openFile(f.path)} className="w-full text-left px-2 py-1.5 rounded-md text-sm text-slate-400 hover:bg-slate-800/50 truncate">{f.path}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle className="w-px bg-slate-800 hover:bg-emerald-500/50 transition-colors" />

        {/* CENTER */}
        <ResizablePanel defaultSize={45} minSize={25}>
          <div className="h-full flex flex-col">
            <div className="flex border-b border-slate-800/70 bg-slate-950/40">
              <button data-testid="center-tab-agents" onClick={() => setCenterTab("agents")}
                className={`h-10 px-5 text-sm transition-colors ${centerTab === "agents" ? "text-emerald-400 border-b-2 border-emerald-400" : "text-slate-500 hover:text-slate-300"}`}>Agents</button>
              <button data-testid="center-tab-code" onClick={() => setCenterTab("code")}
                className={`h-10 px-5 text-sm transition-colors ${centerTab === "code" ? "text-emerald-400 border-b-2 border-emerald-400" : "text-slate-500 hover:text-slate-300"}`}>Code</button>
            </div>

            {centerTab === "agents" ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-auto dc-scroll p-4 space-y-3" data-testid="agent-conversation">
                  {messages.length === 0 && stream.length === 0 && (
                    <div className="text-center text-slate-500 mt-16">
                      <Wand2 className="h-8 w-8 mx-auto mb-3 text-slate-600" />
                      <p>Describe a change and the agents will build it.</p>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div data-testid={`message-${m.role}`} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-emerald-500/15 text-emerald-100" : "bg-slate-800/60 text-slate-200"}`}>{m.content}</div>
                    </div>
                  ))}
                  {stream.length > 0 && <AgentStream stream={stream} onFix={fixError} />}
                  <div ref={streamEndRef} />
                </div>
                <div className="border-t border-slate-800/70 p-3 space-y-2">
                  {suggestions.length > 0 && !running && (
                    <div className="flex flex-wrap gap-1.5" data-testid="smart-suggestions">
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 pr-1"><Lightbulb className="h-3 w-3 text-amber-400" /> Suggestions</span>
                      {suggestions.map((s, i) => (
                        <button key={i} data-testid={`suggestion-${i}`} onClick={() => execRun(s)}
                          className="text-xs px-2.5 py-1 rounded-full border border-slate-700 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300 transition-colors">{s}</button>
                      ))}
                    </div>
                  )}
                  <div className="dc-glass border border-slate-700/70 rounded-xl p-2 flex gap-2">
                    <textarea data-testid="workspace-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} disabled={running}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); execRun(prompt); setPrompt(""); } }}
                      placeholder={files.length ? "Make the header dark, add a contact form..." : "Describe the app to build..."}
                      className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm focus:outline-none placeholder:text-slate-500 disabled:opacity-50" />
                    <button data-testid="workspace-send" disabled={running} onClick={() => { execRun(prompt); setPrompt(""); }}
                      className="h-9 w-9 self-end rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 flex items-center justify-center disabled:opacity-50">
                      {running ? <Loader2 className="h-4 w-4 dc-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex items-center border-b border-slate-800/70 bg-slate-950/40 overflow-x-auto dc-scroll">
                  {tabs.map((t) => (
                    <div key={t} data-testid={`editor-tab-${t}`} onClick={() => { setActiveFile(t); setDiffMode(false); }}
                      className={`flex items-center gap-2 px-3 h-9 text-xs cursor-pointer border-r border-slate-800/70 whitespace-nowrap ${activeFile === t ? "bg-slate-900 text-emerald-300" : "text-slate-400 hover:bg-slate-800/40"}`}>
                      <FileCode className="h-3 w-3" /> {t}
                      <button onClick={(e) => closeTab(e, t)} className="hover:text-rose-400"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {activeFile && (
                    <div className="ml-auto flex items-center gap-1 pr-2">
                      <button data-testid="toggle-diff" onClick={() => setDiffMode((d) => !d)} title="Toggle diff vs last build"
                        className={`h-7 px-2 rounded-md text-xs flex items-center gap-1 ${diffMode ? "text-cyan-400" : "text-slate-400 hover:text-cyan-400"}`}><GitCompare className="h-3.5 w-3.5" /> Diff</button>
                      <button data-testid="save-file" onClick={saveFile} className="h-7 px-2 rounded-md text-xs text-slate-400 hover:text-emerald-400 flex items-center gap-1"><Save className="h-3.5 w-3.5" /> Save</button>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  {!activeFile ? (
                    <div className="h-full flex items-center justify-center text-slate-500 text-sm">Select a file from the explorer.</div>
                  ) : diffMode ? (
                    <DiffEditor height="100%" theme="vs-dark" language={langOf(activeFile)}
                      original={baseline[activeFile] ?? ""} modified={activeContent}
                      options={{ readOnly: true, renderSideBySide: true, minimap: { enabled: false }, fontSize: 13 }} />
                  ) : (
                    <Editor height="100%" theme="vs-dark" language={langOf(activeFile)} value={activeContent} onChange={onEdit}
                      options={{ fontSize: 13, minimap: { enabled: true }, fontFamily: "Fira Code, monospace", scrollBeyondLastLine: false, automaticLayout: true }} />
                  )}
                </div>
              </div>
            )}
          </div>
        </ResizablePanel>

        <ResizableHandle className="w-px bg-slate-800 hover:bg-emerald-500/50 transition-colors" />

        {/* RIGHT PREVIEW */}
        <ResizablePanel defaultSize={35} minSize={22}>
          <div className="h-full flex flex-col bg-slate-950/40">
            <div className="h-10 border-b border-slate-800/70 flex items-center justify-between px-3 gap-2">
              <div className="flex items-center gap-1">
                {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([k, Icon]) => (
                  <button key={k} data-testid={`viewport-${k}`} onClick={() => setViewport(k)}
                    className={`h-7 w-7 rounded-md flex items-center justify-center ${viewport === k ? "bg-emerald-500/15 text-emerald-400" : "text-slate-500 hover:text-slate-300"}`}><Icon className="h-3.5 w-3.5" /></button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <button data-testid="preview-refresh" onClick={() => setPreviewKey((k) => k + 1)} className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:text-emerald-400"><RefreshCw className="h-3.5 w-3.5" /></button>
                <button data-testid="preview-newtab" onClick={() => { const w = window.open(); w.document.write(previewDoc); w.document.close(); }} className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:text-emerald-400"><ExternalLink className="h-3.5 w-3.5" /></button>
                <button data-testid="toggle-console" onClick={() => setShowConsole((s) => !s)} className={`h-7 w-7 rounded-md flex items-center justify-center ${showConsole ? "text-emerald-400" : "text-slate-500 hover:text-slate-300"}`}><TermIcon className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-800/30 flex justify-center p-3">
              {files.length === 0 ? (
                <div className="flex items-center justify-center text-slate-500 text-sm">Live preview appears here after the first build.</div>
              ) : (
                <iframe key={previewKey} data-testid="preview-iframe" title="preview" srcDoc={previewDoc}
                  sandbox="allow-scripts allow-forms allow-modals allow-popups"
                  className="bg-white rounded-lg shadow-2xl transition-all" style={{ width: vpWidth, height: "100%", maxWidth: "100%" }} />
              )}
            </div>
            {showConsole && (
              <div className="h-40 border-t border-slate-800/70 bg-slate-950 overflow-auto dc-scroll p-2 font-mono text-xs" data-testid="console-panel">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">Console</span>
                  <button onClick={() => setConsoleLogs([])} className="text-slate-600 hover:text-slate-400 text-[10px]">clear</button>
                </div>
                {consoleLogs.length === 0 && <div className="text-slate-600">No console output.</div>}
                {consoleLogs.map((l, i) => (
                  <div key={i} className="flex items-start gap-2 py-0.5">
                    <span className={l.level === "error" ? "text-rose-400" : l.level === "warn" ? "text-amber-400" : "text-slate-400"}>{l.msg}</span>
                    {l.level === "error" && <button data-testid="fix-error-btn" onClick={() => fixError(l.msg)} className="ml-auto text-[10px] text-emerald-400 hover:underline shrink-0">Fix with AI</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <OwnerStudio open={studioOpen} onClose={() => setStudioOpen(false)} projectId={id} project={project}
        onLogo={(logo) => setProject((p) => p && { ...p, logo })} onRefresh={loadAll} />
    </div>
  );
}

function AgentStream({ stream, onFix }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3" data-testid="agent-activity">
      <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2"><Loader2 className="h-3 w-3 dc-spin" /> Agent pipeline</div>
      {stream.map((ev, i) => {
        if (ev.type === "agent_start") {
          const Icon = AGENT_ICON[ev.agent] || Sparkles;
          return <div key={i} className="flex items-center gap-2 text-sm"><Icon className={`h-4 w-4 ${AGENT_COLOR[ev.agent]}`} /><span className="font-medium">{ev.agent}</span><span className="text-slate-500">{ev.message}...</span></div>;
        }
        if (ev.type === "agent_output") return <div key={i} className="ml-6 text-xs text-slate-400 bg-slate-950/50 rounded-lg p-2 whitespace-pre-wrap">{ev.content}{ev.model?.provider_name ? ` \u2014 via ${ev.model.provider_name}` : ""}</div>;
        if (ev.type === "plan") return <div key={i} className="ml-6 text-xs text-cyan-300/80"><span className="font-medium">Plan:</span> {(ev.plan.files || []).map((f) => f.path).join(", ")}</div>;
        if (ev.type === "file_written") return <div key={i} className="ml-6 text-xs text-emerald-400 flex items-center gap-1"><FileCode className="h-3 w-3" /> wrote {ev.path}</div>;
        if (ev.type === "file_deleted") return <div key={i} className="ml-6 text-xs text-rose-400">deleted {ev.path}</div>;
        if (ev.type === "security") return <div key={i} className="ml-6 text-xs text-rose-300/80"><ShieldCheck className="h-3 w-3 inline mr-1" />{ev.findings.length ? `${ev.findings.length} finding(s): ${ev.findings.map((f) => f.message).join("; ")}` : "No security issues found"}</div>;
        if (ev.type === "test") return <div key={i} className="ml-6 text-xs text-purple-300/80"><TestTube2 className="h-3 w-3 inline mr-1" />{ev.result.passed ? "All smoke tests passed" : `${ev.result.checks.filter((c) => !c.passed).length} check(s) failed`}</div>;
        if (ev.type === "version_saved") return <div key={i} className="ml-6 text-xs text-slate-400"><GitBranch className="h-3 w-3 inline mr-1" />Saved version v{ev.version}</div>;
        if (ev.type === "task_done") return <div key={i} className="flex items-center gap-2 text-sm text-emerald-400 pt-1"><CheckCircle2 className="h-4 w-4" /> {ev.status === "success" ? "Build complete" : "Build finished with issues"}</div>;
        if (ev.type === "error") return (
          <div key={i} className="ml-6 text-xs text-rose-400 flex items-center gap-2">{ev.message}
            <button onClick={() => onFix(ev.message)} className="text-emerald-400 hover:underline">Fix with AI</button></div>
        );
        return null;
      })}
    </div>
  );
}
